import * as React from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Comms unread state. One provider per company scope (mounted inside
 * AppShell so both the sidebar badge and the Comms page share the same
 * live state).
 *
 * Data flow:
 *   * mount → GET /comms/unread-summary → seeds state
 *   * SSE (window `fable:event` bus) → increment/decrement in place
 *   * markRead(conversation_id) → server + zero locally
 *
 * The provider is safe to mount even if the module is disabled — the
 * summary call will 403 quietly and the state stays at 0/{}.
 */
const CommsUnreadContext = React.createContext(null);

export function CommsUnreadProvider({ children }) {
  const { companyId } = useParams();
  const { user } = useAuth();
  const [total, setTotal] = React.useState(0);
  const [byConv, setByConv] = React.useState({});         // {conv_id: unread}
  const [conversations, setConversations] = React.useState([]);   // last summary snapshot

  const refresh = React.useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/comms/unread-summary`);
      setTotal(data.total_unread || 0);
      const map = {};
      for (const c of data.per_conversation || []) map[c.conversation_id] = c.unread;
      setByConv(map);
      setConversations(data.per_conversation || []);
    } catch { /* module disabled or no access → keep empty state */ }
  }, [companyId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const bump = React.useCallback((convId) => {
    setByConv((prev) => {
      const next = { ...prev, [convId]: (prev[convId] || 0) + 1 };
      return next;
    });
    setTotal((t) => t + 1);
  }, []);

  const clear = React.useCallback((convId) => {
    setByConv((prev) => {
      if (!prev[convId]) return prev;
      const drop = prev[convId];
      const next = { ...prev };
      delete next[convId];
      setTotal((t) => Math.max(0, t - drop));
      return next;
    });
  }, []);

  const markRead = React.useCallback(async (convId, messageId = null) => {
    if (!companyId || !convId) return;
    // Phase 8 — optimistic: clear immediately for that snappy feel; if the
    // server rejects, refresh to reconcile counters back to truth.
    const rollbackSnapshot = { total, byConv: { ...byConv } };
    clear(convId);
    try {
      await api.post(`/companies/${companyId}/comms/conversations/${convId}/read`, {
        message_id: messageId,
      });
    } catch {
      // Reconcile from the server on failure.
      refresh();
      void rollbackSnapshot;   // documented — snapshot kept for future granular rollback
    }
  }, [companyId, clear, refresh, total, byConv]);

  // Subscribe to the AppShell's fable:event bus (SSE fan-out).
  React.useEffect(() => {
    if (!user) return () => {};
    const onEvent = (e) => {
      const evt = e.detail;
      if (!evt || evt.module !== "comms") return;
      if (evt.type === "message.created") {
        const { message, conversation_id } = evt.payload || {};
        if (!message || !conversation_id) return;
        // Skip our own sends (server already marked us read).
        if (message.sender_id === user.id) return;
        bump(conversation_id);
      } else if (evt.type === "conversation.created") {
        // A DM was just opened for me, or a new channel appeared — pick it up.
        refresh();
      }
    };
    window.addEventListener("fable:event", onEvent);
    return () => window.removeEventListener("fable:event", onEvent);
  }, [user, bump, refresh]);

  const value = { total, byConv, conversations, refresh, bump, clear, markRead };
  return <CommsUnreadContext.Provider value={value}>{children}</CommsUnreadContext.Provider>;
}

export function useCommsUnread() {
  const v = React.useContext(CommsUnreadContext);
  if (!v) throw new Error("useCommsUnread must be used inside <CommsUnreadProvider>");
  return v;
}
